import React, { PropsWithChildren } from 'react';
import { ConfigProvider } from '~/core/configuration/configContext';
import { Configuration } from '~/core/configuration/configuration.type';
import { UpdateSearchQuery } from '~/core/useSearchState';
import { ISerpPlugin } from '~/plugins/SerpPlugin';
import { RenderingInfoProvider } from './contexts/useRenderingInfo';
import { IntlContextProvider } from './core/configuration/intlContext';
import { EventToolsProvider } from './core/events/eventsContext';
import { PersistenceProvider } from './core/persistence/PersistenceContext';
import { SERPActionsProvider, SERPActionsProviderProps } from './core/state/actions/SERPActionsContext';
import { SearchQueryProvider } from './core/state/searchQueryContext';
import { SERPStateProvider, SERPStateProviderProps } from './core/state/SERPStateContext';
import { DebugProvider } from './development/debugging';
import { DynamicComponents } from './plugins/dynamicComponents/dynamicComponents';
import { DynamicComponentsProvider } from './plugins/dynamicComponents/dynamicComponentsContext';
import { PluginProvider } from './plugins/pluginContext';

export const RootContexts: React.FC<
    PropsWithChildren & {
        config: Configuration;
        debug: boolean;
        plugin: ISerpPlugin | null;
        dynamicComponents: DynamicComponents;
    }
> = ({ children, debug, config, plugin, dynamicComponents }) => {
    return (
        <DebugProvider debug={debug}>
            <ConfigProvider config={config}>
                <IntlContextProvider>
                    <PluginProvider plugin={plugin}>
                        <EventToolsProvider>
                            <PersistenceProvider>
                                <RenderingInfoProvider>
                                    <DynamicComponentsProvider dynamicComponents={dynamicComponents}>
                                        {children}
                                    </DynamicComponentsProvider>
                                </RenderingInfoProvider>
                            </PersistenceProvider>
                        </EventToolsProvider>
                    </PluginProvider>
                </IntlContextProvider>
            </ConfigProvider>
        </DebugProvider>
    );
};

export const AppContexts: React.FC<
    PropsWithChildren & { updateSearchQuery: UpdateSearchQuery } & SERPStateProviderProps & SERPActionsProviderProps
> = ({ children, updateSearchQuery, state, actions }) => {
    return (
        <SearchQueryProvider searchQuery={state.searchQuery} updateSearchQuery={updateSearchQuery}>
            <SERPStateProvider state={state}>
                <SERPActionsProvider actions={actions}>{children}</SERPActionsProvider>
            </SERPStateProvider>
        </SearchQueryProvider>
    );
};
